import * as React from 'react';
import * as I18n from 'i18n-js';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import { get, flowRight } from 'lodash';
import FormSection from '@reverbdotcom/commons/dist/components/form_section';
import LoadingBars from '@reverbdotcom/commons/dist/components/loading_bars';
import { flashSuccess, flashError } from '@reverbdotcom/commons/dist/flash';
import AlertBox from '@reverbdotcom/commons/dist/components/alert_box';
import FormGroupWithCheckbox from '@reverbdotcom/commons/dist/components/form_group_with_checkbox';
import { CoreUserSubscriptions, UpdateEmailSubscription, rql_EmailSubscription, rql_SubscribeState, SetEmailGlobalSubscribe } from '../schemas/rql';
import { MutationFn } from '../lib/apollo_client';

interface ApolloProps {
  updateEmailSubscription: MutationFn<UpdateEmailSubscription.Mutation, UpdateEmailSubscription.Variables>;
  updateGlobalSubscription: MutationFn<SetEmailGlobalSubscribe.Mutation, SetEmailGlobalSubscribe.Variables>;
}

type IProps = ChildProps<CoreUserSubscriptions.Query> & ApolloProps;

export function Subscriptions({ data, updateEmailSubscription, updateGlobalSubscription }: IProps) {
  const userEmailSubscriptions = get(data, 'me.emailSubscriptions', []) || [];
  const userGlobalEmailSubscribe = get(data, 'me.emailGlobalSubscribe', '');
  const userGloballySubscribed = userGlobalEmailSubscribe === rql_SubscribeState.SUBSCRIBED;
  const disableSubscriptionToggles = data.loading || !!data.error || !userGloballySubscribed;

  const onChangeSubscriptionGroup = React.useCallback(async (subscription: rql_EmailSubscription) => {
    const input = {
      id: subscription.id,
      subscribed: !subscription.subscribed,
      translationKey: subscription.translationKey,
    };

    const response = await updateEmailSubscription({
      variables: { input },
    });

    if (response.errors) {
      flashError(I18n.t('discovery.subscriptions.errorOnUpdate'));
    } else {
      flashSuccess(I18n.t('discovery.subscriptions.success'));
    }
  }, [updateEmailSubscription]);

  const onChangeGlobalStatus = React.useCallback(async () => {
    const state = userGloballySubscribed ? rql_SubscribeState.UNSUBSCRIBED : rql_SubscribeState.SUBSCRIBED;

    const response = await updateGlobalSubscription({
      variables: { input: { state } },
    });

    if (response.errors) {
      flashError(I18n.t('discovery.subscriptions.errorOnUpdate'));
    } else {
      flashSuccess(I18n.t('discovery.subscriptions.success'));
    }
  }, [userGloballySubscribed, updateGlobalSubscription]);

  return (
    <FormSection
      title={I18n.t('discovery.subscriptions.title')}
    >
      {!!data.error && (
        <AlertBox color="red" small>
          {I18n.t('discovery.subscriptions.errorOnLoad')}
        </AlertBox>
      )}
      {data.loading ? (
        <div className="ptb-4 mlr-auto">
          <LoadingBars />
        </div>
      ) : (
        <>
          <div className="scaling-mb-3 mt-1">
            <FormGroupWithCheckbox
              renderAsSwitch
              inputName="global_subscribe"
              checked={userGloballySubscribed}
              updateField={onChangeGlobalStatus}
              label={<span className="weight-bold">{I18n.t('discovery.subscriptions.global')}</span>}
            />
          </div>
          {userEmailSubscriptions.map(subscription => (
            <div className="scaling-mb-2" key={subscription.id}>
              <FormGroupWithCheckbox
                renderAsSwitch
                inputName={subscription.translationKey}
                disabled={disableSubscriptionToggles}
                checked={subscription.subscribed}
                updateField={() => onChangeSubscriptionGroup(subscription)}
                label={I18n.t(`discovery.subscriptions.emails.${subscription.translationKey}`)}
              />
            </div>
          ))}
        </>
      )}
    </FormSection>
  );
}

const subscriptionsQuery = graphql<IProps, CoreUserSubscriptions.Query>(gql`
  query Core_User_Subscriptions {
    me {
      uuid
      emailSubscriptions {
        id
        subscribed
        translationKey
      }
      emailGlobalSubscribe
    }
  }
`);

const updateEmailSubscription =
  graphql<IProps, UpdateEmailSubscription.Mutation, UpdateEmailSubscription.Variables>(
    gql`
      mutation Update_Email_Subscription($input: Input_rql_EmailSubscription) {
        setEmailSubscription(input: $input) {
          id
          subscribed
          translationKey
        }
      }
    `,
    {
      name: 'updateEmailSubscription',
    },
  );

const updateGlobalSubscription =
  graphql<IProps, SetEmailGlobalSubscribe.Mutation, SetEmailGlobalSubscribe.Variables>(
    gql`
      mutation Set_Email_Global_Subscribe($input: Input_rql_SetGlobalSubscribeRequest) {
        setEmailGlobalSubscribe(input: $input) {
          uuid
          emailGlobalSubscribe
        }
      }
    `,
    {
      name: 'updateGlobalSubscription',
    },
  );

export default flowRight([subscriptionsQuery, updateEmailSubscription, updateGlobalSubscription])(Subscriptions);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/subscriptions.tsx